import type { ReactNode } from 'react';
import { Card, Group, ScrollArea, Stack, Table, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

export interface Column<T> {
    key: string;
    title: string;
    render: (row: T) => ReactNode;
    /**
     * Роль колонки в мобильной карточке:
     * title — заголовок карточки, subtitle — строка под ним, field (по умолчанию) — «подпись: значение»,
     * hidden — только на десктопе.
     */
    mobile?: 'title' | 'subtitle' | 'field' | 'hidden';
    width?: number | string;
    align?: 'left' | 'right' | 'center';
}

interface Props<T> {
    data: T[];
    columns: Column<T>[];
    rowKey: (row: T) => string | number;
    actions?: (row: T) => ReactNode;
    onRowClick?: (row: T) => void;
    empty?: ReactNode;
    /** Выделить строку (просрочка, оффлайн и т.п.). */
    highlight?: (row: T) => string | undefined;
}

// Одна декларация колонок — две раскладки: таблица на десктопе, карточки на телефоне.
// Так все списки админки ведут себя одинаково и не требуют отдельной мобильной вёрстки.
export function ResponsiveTable<T>({ data, columns, rowKey, actions, onRowClick, empty, highlight }: Props<T>) {
    const desktop = useMediaQuery('(min-width: 48em)', true, { getInitialValueInEffect: false });

    if (data.length === 0) {
        return (
            <Card>
                <Text c="dimmed" ta="center" py="lg">
                    {empty ?? 'Пусто'}
                </Text>
            </Card>
        );
    }

    if (desktop) {
        return (
            <Card p={0}>
                <ScrollArea type="auto">
                    <Table className="ff-table" verticalSpacing="sm" horizontalSpacing="md" highlightOnHover={!!onRowClick}>
                        <Table.Thead>
                            <Table.Tr>
                                {columns.map((c) => (
                                    <Table.Th key={c.key} w={c.width} ta={c.align}>
                                        {c.title}
                                    </Table.Th>
                                ))}
                                {actions && <Table.Th w={1} />}
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {data.map((row) => (
                                <Table.Tr
                                    key={rowKey(row)}
                                    bg={highlight?.(row)}
                                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                                >
                                    {columns.map((c) => (
                                        <Table.Td key={c.key} ta={c.align}>
                                            {c.render(row)}
                                        </Table.Td>
                                    ))}
                                    {actions && (
                                        <Table.Td onClick={(e) => e.stopPropagation()}>
                                            <Group gap="xs" wrap="nowrap" justify="flex-end">
                                                {actions(row)}
                                            </Group>
                                        </Table.Td>
                                    )}
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>
            </Card>
        );
    }

    const titleCol = columns.find((c) => c.mobile === 'title') ?? columns[0];
    const subtitleCols = columns.filter((c) => c.mobile === 'subtitle');
    const fieldCols = columns.filter((c) => c !== titleCol && (c.mobile ?? 'field') === 'field');

    return (
        <Stack gap="sm">
            {data.map((row) => (
                <Card
                    key={rowKey(row)}
                    padding="md"
                    bg={highlight?.(row)}
                    className={onRowClick ? 'ff-clickable' : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                    <Text fw={700} component="div">
                        {titleCol.render(row)}
                    </Text>
                    {subtitleCols.map((c) => (
                        <Text key={c.key} size="sm" c="dimmed" component="div">
                            {c.render(row)}
                        </Text>
                    ))}
                    {fieldCols.length > 0 && (
                        <Stack gap={6} mt="sm">
                            {fieldCols.map((c) => (
                                <Group key={c.key} justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                                    <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                                        {c.title}
                                    </Text>
                                    <Text size="sm" ta="right" component="div">
                                        {c.render(row)}
                                    </Text>
                                </Group>
                            ))}
                        </Stack>
                    )}
                    {actions && (
                        <Group gap="xs" mt="md" onClick={(e) => e.stopPropagation()}>
                            {actions(row)}
                        </Group>
                    )}
                </Card>
            ))}
        </Stack>
    );
}
